import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';
import PaymentNotificationService from '../../../services/PaymentNotificationService';
import { toast } from 'sonner';

const NotificationSettings = ({ language }) => {
  const [settings, setSettings] = useState({
    enableReminders: true,
    reminderDaysBefore: 7,
    enableOverdueAlerts: true,
    overdueCheckFrequency: 'daily',
    escalationDays: 3,
    notificationChannels: {
      email: true,
      dashboard: true,
      sms: false
    },
    reminderTemplate: 'Rappel : Votre paiement de {amount} pour la facture {invoice_number} est dû le {due_date}.',
    overdueTemplate: 'Paiement en retard : La facture {invoice_number} d\'un montant de {amount} est en retard de {days_overdue} jours.'
  });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);

  const frequencyOptions = [
    { value: 'daily', label: 'Quotidien' },
    { value: 'weekly', label: 'Hebdomadaire' },
    { value: 'monthly', label: 'Mensuel' }
  ];

  // Charger l'utilisateur actuel et ses paramètres
  useEffect(() => {
    loadUserSettings();
  }, []);

  const loadUserSettings = async () => {
    try {
      setLoading(true);

      // Récupérer l'utilisateur actuel
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;

      if (user) {
        setCurrentUserId(user.id);

        // Charger les paramètres depuis Supabase
        const userSettings = await PaymentNotificationService.getNotificationSettings(user.id);
        
        if (userSettings) {
          setSettings({
            enableReminders: userSettings.enable_reminders ?? true,
            reminderDaysBefore: userSettings.reminder_days_before ?? 7,
            enableOverdueAlerts: userSettings.enable_overdue_alerts ?? true,
            overdueCheckFrequency: userSettings.overdue_check_frequency ?? 'daily',
            escalationDays: userSettings.escalation_days ?? 3,
            notificationChannels: userSettings.notification_channels ?? {
              email: true,
              dashboard: true,
              sms: false
            },
            reminderTemplate: userSettings.reminder_template ?? 'Rappel : Votre paiement de {amount} pour la facture {invoice_number} est dû le {due_date}.',
            overdueTemplate: userSettings.overdue_template ?? 'Paiement en retard : La facture {invoice_number} d\'un montant de {amount} est en retard de {days_overdue} jours.'
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUserId) {
      toast.error('Utilisateur non connecté');
      return;
    }

    try {
      setLoading(true);

      // Sauvegarder dans Supabase
      await PaymentNotificationService.saveNotificationSettings(currentUserId, settings);

      setSaved(true);
      toast.success('Paramètres enregistrés avec succès');
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de l\'enregistrement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" size={32} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Chargement des paramètres...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Reminder Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Icon name="Bell" size={24} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              Rappels de paiement
            </h3>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings?.enableReminders}
              onChange={(e) => setSettings({ ...settings, enableReminders: e?.target?.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        {settings?.enableReminders && (
          <div className="space-y-4">
            <Input
              label="Jours avant échéance"
              type="number"
              min="1"
              max="30"
              value={settings?.reminderDaysBefore}
              onChange={(e) => setSettings({ ...settings, reminderDaysBefore: parseInt(e?.target?.value) })}
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Template de message
              </label>
              <textarea
                value={settings?.reminderTemplate}
                onChange={(e) => setSettings({ ...settings, reminderTemplate: e?.target?.value })}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Variables disponibles: {'{amount}'}, {'{invoice_number}'}, {'{due_date}'}, {'{client_name}'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Overdue Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" size={24} className="text-warning" />
            <h3 className="text-lg font-semibold text-foreground">
              Alertes de retard
            </h3>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings?.enableOverdueAlerts}
              onChange={(e) => setSettings({ ...settings, enableOverdueAlerts: e?.target?.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        {settings?.enableOverdueAlerts && (
          <div className="space-y-4">
            <Select
              label="Fréquence de vérification"
              options={frequencyOptions}
              value={settings?.overdueCheckFrequency}
              onChange={(value) => setSettings({ ...settings, overdueCheckFrequency: value })}
            />
            <Input
              label="Jours avant escalade"
              type="number"
              min="1"
              max="30"
              value={settings?.escalationDays}
              onChange={(e) => setSettings({ ...settings, escalationDays: parseInt(e?.target?.value) })}
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Template d'alerte
              </label>
              <textarea
                value={settings?.overdueTemplate}
                onChange={(e) => setSettings({ ...settings, overdueTemplate: e?.target?.value })}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Variables disponibles: {'{amount}'}, {'{invoice_number}'}, {'{days_overdue}'}, {'{client_name}'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Notification Channels */}
     

      {/* Save Button */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} fullWidth loading={loading} disabled={loading}>
          <Icon name="Save" size={18} className="mr-2" />
          Enregistrer les paramètres
        </Button>
        {saved && (
          <div className="flex items-center gap-2 text-success">
            <Icon name="CheckCircle" size={20} />
            <span className="text-sm font-medium">
              Enregistré
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;